package com.trevorwiebe.timesheet.mileage.data.dto

import com.trevorwiebe.timesheet.core.domain.Util
import com.trevorwiebe.timesheet.mileage.domain.model.MileageEntry
import com.trevorwiebe.timesheet.mileage.domain.model.TripType
import com.trevorwiebe.timesheet.mileage.domain.model.TripTypeData
import kotlinx.datetime.TimeZone
import kotlinx.serialization.Serializable

@Serializable
data class MileageEntryDto(
    val date: String = "",
    val employeeId: String = "",
    val truckId: String = "",
    val trailerId: String? = null,
    val mileageValue: Double = 0.0,
    val tripType: String = "",
    // STATE_LINE fields
    val fromState: String? = null,
    val toState: String? = null,
    val highway: String? = null,
    // FUEL fields
    val gallons: Double? = null,
    val receiptImageUrl: String? = null,
    // FUEL / LOADING / UNLOADING / OTHER fields
    val locationId: String? = null,
    // LOADING / UNLOADING fields
    val loadingUnloadingNotes: String? = null,
    // SERVICE fields
    val serviceLocation: String? = null,
    // OTHER fields
    val otherNotes: String? = null,
    // START_END fields
    val startEndLocation: String? = null
)

fun MileageEntryDto.toMileageEntry(id: String): MileageEntry {
    val tripTypeEnum = TripType.valueOf(tripType)
    val data: TripTypeData = when (tripTypeEnum) {
        TripType.STATE_LINE -> TripTypeData.StateLine(fromState ?: "", toState ?: "", highway ?: "")
        TripType.FUEL       -> TripTypeData.Fuel(locationId ?: "", gallons ?: 0.0, receiptImageUrl)
        TripType.LOADING    -> TripTypeData.Loading(locationId ?: "", loadingUnloadingNotes ?: "")
        TripType.UNLOADING  -> TripTypeData.Unloading(locationId ?: "", loadingUnloadingNotes ?: "")
        TripType.SERVICE    -> TripTypeData.Service(serviceLocation ?: "")
        TripType.OTHER      -> TripTypeData.Other(otherNotes ?: "", locationId?.takeIf { it.isNotBlank() })
        TripType.START_END  -> TripTypeData.StartEnd(startEndLocation ?: "")
    }
    return MileageEntry(
        id = id,
        date = Util.convertStringToLocalDateTime(date),
        employeeId = employeeId,
        equipmentId = truckId,
        trailerId = trailerId,
        mileageValue = mileageValue.toLong(),
        tripType = tripTypeEnum,
        tripTypeData = data
    )
}

fun MileageEntry.toMileageEntryDto(): MileageEntryDto {
    val base = MileageEntryDto(
        date = with(Util) { date.toIsoStringWithMillis(TimeZone.currentSystemDefault()) },
        employeeId = employeeId,
        truckId = equipmentId,
        trailerId = trailerId,
        mileageValue = mileageValue.toDouble(),
        tripType = tripType.name
    )
    return when (val d = tripTypeData) {
        is TripTypeData.StateLine -> base.copy(fromState = d.fromState, toState = d.toState, highway = d.highway.ifBlank { null })
        is TripTypeData.Fuel      -> base.copy(locationId = d.locationId, gallons = d.gallons, receiptImageUrl = d.receiptImageUrl)
        is TripTypeData.Loading   -> base.copy(locationId = d.locationId, loadingUnloadingNotes = d.notes.ifBlank { null })
        is TripTypeData.Unloading -> base.copy(locationId = d.locationId, loadingUnloadingNotes = d.notes.ifBlank { null })
        is TripTypeData.Service   -> base.copy(serviceLocation = d.serviceLocation)
        is TripTypeData.Other     -> base.copy(otherNotes = d.notes, locationId = d.locationId?.ifBlank { null })
        is TripTypeData.StartEnd  -> base.copy(startEndLocation = d.location)
    }
}
